import {
  Box,
  Button,
  Flex,
  HStack,
  Icon,
  IconButton,
  Spacer,
  Tag,
  Text,
  Tooltip,
} from "@chakra-ui/react";
import React from "react";
import {
  RiCheckLine,
  RiCloseLine,
  RiEyeOffLine,
  RiInformationLine,
} from "react-icons/ri";
import { useUserPermissions } from "src/hooks/useUserPermissions";
import * as AF from "src/types/formTemplate";
import * as GQL from "src/types/graphql";
import { AdminQuestionAnswer } from "./QuestionAnswer";

type VerificationSectionProps = {
  verification: AF.FormVerification<AF.WithId>;
  questions: AF.Question<AF.WithId>[];
  answers: Record<string, any>;
  form: GQL.GetFormViewById_form_by_pk;
  onVerificationClick: () => void;
  verificationHandler: (
    formId: string,
    verificationId: string,
    verificationLabel: string,
    status: GQL.verification_status_enum
  ) => Promise<void>;
  isVerificationModalEnabled: boolean;
  hasFileUploads?: boolean;
  isSubmitting: boolean;
  applicableQuestionIdSet?: Set<string>;
};

export const VerificationSection: React.FC<VerificationSectionProps> = ({
  verification,
  questions,
  answers,
  form,
  onVerificationClick,
  verificationHandler,
  isVerificationModalEnabled,
  hasFileUploads = false,
  isSubmitting,
  applicableQuestionIdSet,
}) => {
  const userPermissions = useUserPermissions();
  const verificationResult = form.form_verification_results.find(
    (result) => result.form_verification.id === verification.id
  );
  const isVerified =
    verificationResult?.verification_status ===
    GQL.verification_status_enum.Verified;
  const isRejected =
    verificationResult?.verification_status ===
    GQL.verification_status_enum.Rejected;

  const tooltipVerifyText =
    "If not, input the right value if you know it or reach out to the family for the correct information";
  const tooltipLockedText =
    "This information has been verified and can no longer be edited by a parent/guardian";

  return (
    <Flex
      direction="column"
      border="1px solid"
      borderColor="gray.200"
      borderRadius="md"
      minW="30rem"
      width="75%"
      p={4}
      gap={4}
      alignItems="flex-start"
    >
      <Flex gap={2} width="100%" alignItems="center">
        <Flex gap={2}>
          <Tag
            size="sm"
            variant="solid"
            color="blackAlpha.900"
            bg="gray.200"
            lineHeight={1.3}
          >
            {verification?.label} verification
          </Tag>
          <Box
            visibility={!isVerified ? "hidden" : undefined}
            bgColor="gray.500"
            borderRadius="2"
            paddingLeft={1}
            paddingRight={1}
            fontSize="sm"
            fontWeight="700"
            textColor="white"
          >
            <Tooltip
              label={<Text align="center">{tooltipLockedText}</Text>}
              placement="top"
              hasArrow
              arrowSize={15}
              gutter={20}
              minW="22rem"
            >
              LOCKED
            </Tooltip>
          </Box>
        </Flex>
        <Spacer />
        {isVerificationModalEnabled && hasFileUploads && (
          <Button
            size="sm"
            variant="outline"
            colorScheme="primary"
            onClick={onVerificationClick}
          >
            View verification
          </Button>
        )}
      </Flex>

      {/* Render all questions */}
      {questions.map((question, qidx) => {
        const isHidden = !applicableQuestionIdSet?.has(question.id);
        return (
          <Box key={qidx} maxWidth="100%" display="flex">
            <AdminQuestionAnswer
              marginBottom={4}
              question={question}
              answer={answers[question.id]}
              formId={form.id}
            />
            {isHidden ? (
              <Tooltip label="Question is hidden">
                <Box>
                  <Icon as={RiEyeOffLine} color="blackAlpha.500" />
                </Box>
              </Tooltip>
            ) : null}
          </Box>
        );
      })}

      <Flex w="100%" gap={1}>
        <HStack>
          <Text lineHeight="2">
            Is the {verification.label.toLocaleLowerCase()} information correct?
          </Text>
          <Tooltip
            label={<Text align="center">{tooltipVerifyText}</Text>}
            placement="top"
            hasArrow
            arrowSize={15}
            gutter={20}
            minW="22rem"
          >
            <IconButton
              size="sm"
              aria-label={tooltipVerifyText}
              icon={<RiInformationLine />}
              variant="ghost"
            />
          </Tooltip>
        </HStack>
        <Spacer width={6} />
        <Box>
          <Flex gap={2}>
            <Button
              isDisabled={!userPermissions.hasOne("form:update")}
              size="sm"
              colorScheme="gray"
              color="gray.700"
              leftIcon={<RiCloseLine />}
              variant={isRejected ? "solid" : "outline"}
              isLoading={isSubmitting}
              onClick={() =>
                verificationHandler(
                  form.id,
                  verification.id,
                  verification.label,
                  isRejected
                    ? GQL.verification_status_enum.Pending
                    : GQL.verification_status_enum.Rejected
                )
              }
            >
              No
            </Button>
            <Button
              isDisabled={!userPermissions.hasOne("form:update")}
              size="sm"
              colorScheme="gray"
              color="gray.700"
              leftIcon={<RiCheckLine />}
              variant={isVerified ? "solid" : "outline"}
              isLoading={isSubmitting}
              onClick={() =>
                verificationHandler(
                  form.id,
                  verification.id,
                  verification.label,
                  isVerified
                    ? GQL.verification_status_enum.Pending
                    : GQL.verification_status_enum.Verified
                )
              }
            >
              Yes
            </Button>
          </Flex>
        </Box>
      </Flex>
    </Flex>
  );
};
